import {useCallback, useEffect, useEffectEvent, useMemo, useState} from 'react';
import {assert} from 'assert-ts';
import dayjs from 'dayjs';
import type {HttpError} from 'api/http/types';
import type {DataLoadStatus} from 'api/types';
import {useLocalization} from 'localization';
import {getDummyUser} from 'services/users/functions';
import {useGetUsersQuery} from 'services/users/usersApi';
import {useLoadError} from 'services/utils';
import {useSnacks} from 'components';
import type {Note, NoteContextType, NoteData} from '../types';
import {
  useDeleteNoteMutation,
  useGetNotesQuery,
  usePostNoteMutation,
  usePutNoteMutation,
} from '../notesApi';

/**
 * @deprecated Use noteApi hooks directly instead.
 */
export const useLazyNotes = (
  contextId: string,
  doLoad: boolean,
  contextType: NoteContextType,
  _mock?: boolean,
): {
  notes: DataLoadStatus<Note[]>;
  createNote: (note: string) => Promise<void>;
  updateNote: (id: string, note: string) => Promise<void>;
  removeNote: (id: string) => Promise<void>;
} => {
  const {errorSnack} = useSnacks();
  const {t} = useLocalization();

  const {data: users} = useGetUsersQuery();

  // Notes loaded from backend
  const {
    data: loadedNotes,
    isLoading,
    isSuccess,
    isError,
    error: loadError,
  } = useGetNotesQuery(contextId, {skip: !doLoad});

  const loadedNotesData: DataLoadStatus<NoteData[]> = useMemo(() => {
    if (isSuccess && loadedNotes) {
      return {
        status: 'Loaded',
        data: [...loadedNotes].sort((a, b) => b.lastModified - a.lastModified),
      };
    }
    if (isError) {
      return {status: 'Failed', error: loadError as HttpError};
    }
    if (isLoading) {
      return {status: 'Loading'};
    }
    return {status: 'NotLoaded'};
  }, [isSuccess, isError, isLoading, loadedNotes, loadError]);

  useLoadError(loadedNotesData.status, 'error.notes.failedToLoad');

  const [postNote] = usePostNoteMutation();
  const [putNote] = usePutNoteMutation();
  const [deleteNote] = useDeleteNoteMutation();

  // Notes created/updated, may overlap with notes loaded from backend
  const [editedNotesData, setEditedNotesData] = useState<NoteData[]>([]);

  // Note ids removed, may overlap with notes loaded from backend or created/updated
  const [removedNoteIds, setRemovedNoteIds] = useState<string[]>([]);

  // Merged [editedNotes, loadedNotes] x users
  const [notes, setNotes] = useState<DataLoadStatus<Note[]>>({
    status: 'NotLoaded',
  });
  const onSetNotes = useEffectEvent(setNotes);

  // Create new note: added to edited notes and posted to backend
  const createNote = useCallback(
    (message: string) => {
      assert(
        loadedNotesData.status === 'Loaded',
        'useLazyNotes.createNote: notes can only be created after notes from backend have been successfully loaded',
      );

      const lastModified = dayjs();
      const tempId = new Date().getTime().toString();
      const newNote: NoteData = {
        id: tempId, // Will be set by backend
        // TODO: Get current user, invalid jwt-token, both idToken and accessToken, cannot get user id
        userId: '1',
        contextId,
        contextType,
        created: lastModified.valueOf(),
        modified: lastModified.valueOf(),
        lastModified: lastModified.valueOf(),
        message,
      };

      return postNote(newNote)
        .unwrap()
        .then(resultNote => {
          setEditedNotesData(notes => [resultNote, ...notes]);
        })
        .catch(error => {
          errorSnack(t('error.notes.failedToCreateNote'));
          throw error;
        });
    },
    [loadedNotesData.status, contextId, contextType, postNote, errorSnack, t],
  );

  const updateNote = useCallback(
    (id: string, note: string) => {
      assert(
        loadedNotesData.status === 'Loaded',
        'useLazyNotes.updateNote: notes can only be created after notes from backend have been successfully loaded',
      );

      const oldNote = assert(
        loadedNotesData.data.find(l => l.id === id) ??
          editedNotesData.find(e => e.id === id),
        'useLazyNotes.updateNote: note to be updated not found',
      );

      const updatedNote: NoteData = {
        ...oldNote,
        message: note,
        lastModified: dayjs().valueOf(),
      };

      return putNote(updatedNote)
        .unwrap()
        .then(resultNote => {
          setEditedNotesData(notes => [
            resultNote,
            // Filter out previous edits to same note
            ...notes.filter(n => n.id !== id),
          ]);
        })
        .catch(error => {
          errorSnack(t('error.notes.failedToUpdatesNote'));
          throw error;
        });
    },
    [
      putNote,
      editedNotesData,
      errorSnack,
      loadedNotesData.data,
      loadedNotesData.status,
      t,
    ],
  );

  const removeNote = useCallback(
    (id: string) => {
      assert(
        loadedNotesData.status === 'Loaded',
        'useLazyNotes.deleteNote: notes can only be deleted after notes from backend have been successfully loaded',
      );

      return deleteNote({id, contextId})
        .unwrap()
        .then(() => {
          setRemovedNoteIds(ids => [...ids, id]);
        })
        .catch(error => {
          errorSnack(t('error.notes.failedToDeleteNote'));
          throw error;
        });
    },
    [deleteNote, errorSnack, loadedNotesData.status, t, contextId],
  );

  // Combine noteData and users into (note, user)
  useEffect(() => {
    if (loadedNotesData.status === 'Loaded') {
      // TODO: Load missing users
      const notesData = [
        ...editedNotesData,
        // Filter out notes that have been edited
        ...loadedNotesData.data.filter(
          l => !editedNotesData.find(e => e.id === l.id),
        ),
      ];

      onSetNotes(() => ({
        status: 'Loaded' as const,
        data: notesData
          .map(d => {
            return {
              note: d,
              user:
                users?.find(u => u.id === d.userId) ?? getDummyUser(d.userId),
            };
          })
          .filter(n => !removedNoteIds.includes(n.note.id)),
      }));
    } else {
      onSetNotes(old =>
        loadedNotesData.status === old.status
          ? old
          : loadedNotesData.status === 'Failed'
            ? {
                status: 'Failed',
                error: loadedNotesData.error,
                old: old.data,
              }
            : loadedNotesData.status === 'Loading'
              ? {
                  status: 'Loading',
                  error: old.error,
                  old: old.data,
                }
              : old,
      );
    }
  }, [editedNotesData, loadedNotesData, removedNoteIds, users]);

  return useMemo(
    () => ({
      notes,
      createNote,
      updateNote,
      removeNote,
    }),
    [createNote, notes, removeNote, updateNote],
  );
};
